import type { GeoJSONSource, Map as MapLibreMap } from 'maplibre-gl'
import { STATUS_COLOURS, tokens } from '../../theme'
import { CLUSTER_MAX_ZOOM } from './PlotClusters'

export const PLOTS_SOURCE = 'plots'
export const PLOTS_FILL = 'plots-fill'
export const PLOTS_LINE = 'plots-line'
export const PLOTS_SELECTED = 'plots-selected'
export const BOUNDARY_SOURCE = 'country-boundary'
export const BOUNDARY_LINE = 'country-boundary-line'

const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

/** Fill colour by status: the token colours at 30–40% alpha over a solid stroke. */
const fillByStatus = [
  'match', ['get', 'status'],
  'passed', STATUS_COLOURS.passed.fill,
  'flagged', STATUS_COLOURS.flagged.fill,
  'rejected', STATUS_COLOURS.rejected.fill,
  'excluded', STATUS_COLOURS.excluded.fill,
  STATUS_COLOURS.passed.fill,
] as const

const strokeByStatus = [
  'match', ['get', 'status'],
  'passed', STATUS_COLOURS.passed.stroke,
  'flagged', STATUS_COLOURS.flagged.stroke,
  'rejected', STATUS_COLOURS.rejected.stroke,
  'excluded', STATUS_COLOURS.excluded.stroke,
  STATUS_COLOURS.passed.stroke,
] as const

/** Adds the plot and boundary layers once; data arrives through `setPlots`. */
export function addPlotLayers(map: MapLibreMap): void {
  if (map.getSource(PLOTS_SOURCE)) return

  map.addSource(BOUNDARY_SOURCE, { type: 'geojson', data: EMPTY })
  map.addLayer({
    id: BOUNDARY_LINE,
    type: 'line',
    source: BOUNDARY_SOURCE,
    paint: {
      'line-color': tokens.color.ink500,
      'line-width': 1.5,
      'line-dasharray': [6, 5],
    },
  })

  map.addSource(PLOTS_SOURCE, { type: 'geojson', data: EMPTY, promoteId: 'id' })
  // Below this the polygons are a scatter of single pixels and the clustered
  // points stand in for them; see PlotClusters.
  map.addLayer({
    id: PLOTS_FILL,
    type: 'fill',
    source: PLOTS_SOURCE,
    minzoom: CLUSTER_MAX_ZOOM,
    paint: { 'fill-color': fillByStatus as never },
  })
  map.addLayer({
    id: PLOTS_LINE,
    type: 'line',
    source: PLOTS_SOURCE,
    minzoom: CLUSTER_MAX_ZOOM,
    paint: {
      'line-color': strokeByStatus as never,
      'line-width': ['case', ['boolean', ['feature-state', 'selected'], false], 2.5, 1.2],
    },
  })
  map.addLayer({
    id: PLOTS_SELECTED,
    type: 'line',
    source: PLOTS_SOURCE,
    minzoom: CLUSTER_MAX_ZOOM,
    filter: ['==', ['get', 'id'], ''],
    paint: { 'line-color': tokens.color.brandDeep, 'line-width': 2.5 },
  })
}

export function setPlots(map: MapLibreMap, data: GeoJSON.FeatureCollection | undefined): void {
  const source = map.getSource(PLOTS_SOURCE)
  if (source && 'setData' in source) (source as GeoJSONSource).setData(data ?? EMPTY)
}

export function setBoundary(map: MapLibreMap, data: GeoJSON.FeatureCollection | undefined): void {
  const source = map.getSource(BOUNDARY_SOURCE)
  if (source && 'setData' in source) (source as GeoJSONSource).setData(data ?? EMPTY)
}

/**
 * The boundary overlay earns its place on imagery and not on a street map.
 *
 * The street basemap already draws national borders, so ours lands on top
 * of them as a second, slightly different line — which reads as a
 * disagreement about where the border is rather than as one border. On
 * satellite there is nothing underneath, and the boundary is the whole
 * point of the within-country check, so it stays.
 */
export function setBoundaryVisible(map: MapLibreMap, visible: boolean): void {
  if (!map.getLayer(BOUNDARY_LINE)) return
  map.setLayoutProperty(BOUNDARY_LINE, 'visibility', visible ? 'visible' : 'none')
}

export function setSelected(map: MapLibreMap, id: string | null): void {
  if (!map.getLayer(PLOTS_SELECTED)) return
  map.setFilter(PLOTS_SELECTED, ['==', ['get', 'id'], id ?? ''])
}
